import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, HelpCircle, Shield, FileText, Bell, LogOut, 
  ChevronRight, Pause, Trash2, CreditCard, AlertTriangle, Info, 
  Mail, Sun, Moon, Monitor, Palette, BarChart3, Gift, Eye, MapPin, Plane, Heart
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import QuickFlip from '../components/QuickFlip';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * Settings Page - LikeKind (LOCKED STRUCTURE)
 * 
 * Navigation:
 * 1. Account
 * 2. Appearance
 * 3. About
 * 4. Account Management
 * 5. Privacy & Trust
 * 
 * All buttons must work or redirect with guidance.
 */

export default function Settings() {
  const navigate = useNavigate();
  const { user, setUser, setShowFounderWelcome } = useContext(AuthContext);
  const { theme, setTheme } = useTheme();
  const [showQuickFlip, setShowQuickFlip] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBreakConfirm, setShowBreakConfirm] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [breakStatus, setBreakStatus] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchAccountStatus();
      fetchUserStats();
    }
  }, [user]);

  const fetchAccountStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const [previewRes, breakRes] = await Promise.all([
        axios.get(`${API}/api/account/delete-preview`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/api/account/break-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setDeletePreview(previewRes.data);
      setBreakStatus(breakRes.data);
    } catch (error) {
      console.error('Error fetching account status:', error);
    }
  };

  const fetchUserStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/user/stats`, { 
        headers: { Authorization: `Bearer ${token}` } 
      }).catch(() => ({ data: null }));
      setUserStats(res.data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const [commonsGroups, setCommonsGroups] = useState([]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/api/commons/my-commons`, { withCredentials: true })
      .then((r) => setCommonsGroups(r.data?.commons || []))
      .catch(() => setCommonsGroups([]));
  }, [user]);

  const stopCommons = async (groupId, groupName) => {
    if (!window.confirm(`Stop keeping "${groupName}" going?`)) return;
    try {
      await axios.post(
        `${API}/api/commons/${groupId}/cancel`,
        {},
        { withCredentials: true },
      );
      toast.success('Stopped. It will stay active until the current period ends.');
      // Refresh the list
      const r = await axios.get(`${API}/api/commons/my-commons`, { withCredentials: true });
      setCommonsGroups(r.data?.commons || []);
    } catch {
      toast.error("Couldn't stop. Try again.");
    }
  };

  const handleSignOut = async () => {
    try {
      // Call logout endpoint to reset online status + revoke server session + clear HttpOnly cookies
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/api/auth/logout`, {}, {
        headers,
        withCredentials: true,
      }).catch(() => {}); // Don't block on failure
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      toast.success('Signed out');
      // Zero-friction: drop user back into Base Camp (guest state), never a dead-end login screen.
      navigate('/base-camp');
    }
  };

  const handleTakeBreak = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/account/break`, { duration_days: 30 }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Account paused. See you when you return!');
      fetchAccountStatus();
      setShowBreakConfirm(false);
    } catch (error) {
      toast.error('Failed to pause account');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeAccount = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/account/resume`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Welcome back! Your account is active.');
      fetchAccountStatus();
    } catch (error) {
      toast.error('Failed to resume account');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/account/delete`, { confirm: true }, { headers: { Authorization: `Bearer ${token}` } });
      
      localStorage.removeItem('token');
      setUser(null);
      toast.success('Account deleted. Thank you for being part of LikeKind.');
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  if (showQuickFlip) {
    return <QuickFlip onComplete={() => setShowQuickFlip(false)} />;
  }

  const isPaidMember = deletePreview?.is_paid_member;
  const isOnBreak = breakStatus?.on_break;

  // Settings sections — Final Structure (aligned with product + trust model)
  // Order: Account, Appearance, Membership, Privacy & Trust, About LikeKind, Discover
  const sections = [
    {
      title: '1. Account',
      items: user ? [
        { icon: User, label: 'Edit Backpack', action: () => navigate('/settings/my-world'), chevron: true },
        { icon: MapPin, label: 'Location Settings', sublabel: user.travel_mode_enabled ? `Traveling in ${user.travel_location?.state || 'Unknown'}` : (user.base_state || 'Set your location'), action: () => navigate('/settings/location'), chevron: true },
        { icon: Shield, label: 'Security', sublabel: 'Password & devices', action: () => navigate('/settings/security'), chevron: true, testId: 'settings-security' },
      ] : [
        { icon: User, label: 'Sign In', action: () => navigate('/auth?mode=signin'), chevron: true },
      ]
    },
    {
      title: '2. Appearance',
      items: [
        { 
          icon: theme === 'dark' ? Moon : Sun, 
          label: 'Theme', 
          sublabel: theme.charAt(0).toUpperCase() + theme.slice(1),
          action: () => {
            const themes = ['light', 'dark', 'system'];
            const currentIndex = themes.indexOf(theme);
            setTheme(themes[(currentIndex + 1) % themes.length]);
          },
          chevron: false
        },
      ]
    },
    {
      title: '3. Membership',
      items: user ? [
        { icon: CreditCard, label: 'Current Plan', sublabel: user.membership_tier || 'Open Member', action: () => navigate('/membership'), chevron: true },
      ] : [
        { icon: CreditCard, label: 'View Plans', action: () => navigate('/membership'), chevron: true },
      ]
    },
    {
      title: '4. Privacy & Trust',
      items: [
        { icon: Shield, label: 'Privacy Policy', action: () => navigate('/privacy'), chevron: true },
        { icon: Shield, label: 'Trust & Safety', action: () => navigate('/trust'), chevron: true },
        { icon: Eye, label: 'Parent Visibility & Child Safety', action: () => navigate('/parent-visibility'), chevron: true },
        { icon: FileText, label: 'Terms of Service', action: () => navigate('/terms'), chevron: true },
      ]
    },
    {
      title: '5. About LikeKind',
      items: [
        { icon: Info, label: 'What is LikeKind', action: () => navigate('/what-is-likekind'), chevron: true },
        { icon: HelpCircle, label: 'How It Works', action: () => navigate('/how-it-works'), chevron: true },
        { icon: HelpCircle, label: 'FAQ', action: () => navigate('/faq'), chevron: true },
        { icon: Heart, label: "Founder's Letter", action: () => navigate('/founder-statement'), chevron: true },
        { icon: Mail, label: 'Contact Support', action: () => navigate('/support'), chevron: true },
      ]
    },
    {
      title: '6. Discover',
      items: [
        { icon: Heart, label: 'Become a Supporter', sublabel: 'Support local activity', action: () => navigate('/become-supporter'), chevron: true },
        // "For Schools & Institutions" entry removed — /for/institutions unrouted pre-deploy.
      ]
    },
    {
      title: 'Account Management',
      items: user ? [
        ...(isOnBreak ? [
          { icon: Pause, label: 'Resume Account', sublabel: 'Your account is paused', action: handleResumeAccount, variant: 'positive' }
        ] : [
          { icon: Pause, label: 'Take a Break', sublabel: 'Pause for up to 30 days', action: () => setShowBreakConfirm(true) }
        ]),
        { icon: Trash2, label: 'Delete Account', sublabel: 'This cannot be undone', action: () => setShowDeleteConfirm(true), variant: 'danger' },
      ] : []
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            {/* FIX: Always navigate to home, not -1, to prevent navigation loops */}
            <button onClick={() => navigate('/')} className="p-2 -ml-2 rounded-lg hover:opacity-70 transition-opacity">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Settings</h1>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* User Profile Card (if logged in) */}
        {user && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
            <div className="flex items-center gap-3">
              <img 
                src={user.picture || '/default-avatar.png'} 
                alt="" 
                className="w-14 h-14 rounded-full object-cover"
              />
              <div className="flex-1">
                <p className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                  @{user.username || 'user'}
                </p>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  {user.email}
                </p>
                {isPaidMember && (
                  <span className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block bg-[var(--lk-sage)] text-white">
                    {user.membership_tier || 'Member'}
                  </span>
                )}
              </div>
              <button
                onClick={() => navigate('/me')}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <ChevronRight className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
              </button>
            </div>
          </Card>
        )}

        {/* User Stats (if logged in) */}
        {user && userStats && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
              <h3 className="font-semibold text-sm" style={{ color: 'var(--fy-text-primary)' }}>Your Stats</h3>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xl font-bold" style={{ color: 'var(--lk-sage)' }}>{userStats.connections || 0}</p>
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Contacts</p>
              </div>
              <div>
                <p className="text-xl font-bold" style={{ color: 'var(--lk-sage)' }}>{userStats.posts || 0}</p>
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Posts</p>
              </div>
              <div>
                <p className="text-xl font-bold" style={{ color: 'var(--lk-sage)' }}>{userStats.groups || 0}</p>
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Groups</p>
              </div>
            </div>
          </Card>
        )}

        {/* Commons — groups you're keeping going */}
        {commonsGroups.length > 0 && (
          <div data-testid="settings-commons-section">
            <h2 className="text-xs font-semibold uppercase tracking-wide mb-2 px-1" style={{ color: 'var(--fy-text-secondary)' }}>
              Commons
            </h2>
            <Card style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
              {commonsGroups.map((g, idx) => {
                const isLast = idx === commonsGroups.length - 1;
                return (
                  <div
                    key={g.group_id}
                    className={`flex items-center gap-3 p-4 ${!isLast ? 'border-b' : ''}`}
                    style={{ borderColor: 'var(--fy-border-subtle)' }}
                    data-testid={`settings-commons-${g.group_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>
                        {g.name}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                        Keeping this group going
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => stopCommons(g.group_id, g.name)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-80"
                      style={{ backgroundColor: 'var(--fy-surface-card)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
                      data-testid={`settings-commons-stop-${g.group_id}`}
                    >
                      Stop keeping this going
                    </button>
                  </div>
                );
              })}
            </Card>
          </div>
        )}

        {/* Settings Sections */}
        {sections.map((section, sectionIdx) => (
          section.items.length > 0 && (
            <div key={sectionIdx}>
              <h2 className="text-xs font-semibold uppercase tracking-wide mb-2 px-1" style={{ color: 'var(--fy-text-secondary)' }}>
                {section.title}
              </h2>
              <Card style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
                {section.items.map((item, itemIdx) => {
                  const Icon = item.icon;
                  const isLast = itemIdx === section.items.length - 1;
                  const textColor = item.variant === 'danger' ? 'var(--fy-accent-emphasis)' : 
                                   item.variant === 'positive' ? 'var(--lk-sage)' : 
                                   'var(--fy-text-primary)';
                  
                  return (
                    <button
                      key={itemIdx}
                      onClick={item.action}
                      className={`w-full flex items-center gap-3 p-4 hover:opacity-80 transition-opacity text-left ${!isLast ? 'border-b' : ''}`}
                      style={{ borderColor: 'var(--fy-border-subtle)' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: textColor }} />
                      <div className="flex-1">
                        <p className="font-medium" style={{ color: textColor }}>{item.label}</p>
                        {item.sublabel && (
                          <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{item.sublabel}</p>
                        )}
                      </div>
                      {item.chevron && (
                        <ChevronRight className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
                      )}
                    </button>
                  );
                })}
              </Card>
            </div>
          )
        ))}

        {/* Sign Out */}
        {user && (
          <Button
            onClick={handleSignOut}
            variant="outline"
            className="w-full"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        )}

        {/* Version */}
        <p className="text-center text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
          LikeKind v1.0.0 • Made with care
        </p>
      </main>

      {/* Take a Break Confirmation Modal */}
      {showBreakConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm p-6" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
            <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Take a Break?</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Your profile will be hidden and you will not receive notifications. You can come back anytime within 30 days.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowBreakConfirm(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-[var(--lk-sage)]" 
                onClick={handleTakeBreak}
                disabled={loading}
              >
                {loading ? 'Pausing...' : 'Take a Break'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm p-6" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Delete Account?</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              This will permanently delete your account, profile, posts, photos, and all data. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-500 hover:bg-red-600 text-white" 
                onClick={handleDeleteAccount}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Forever'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
